import { dayjsWithTimezone, MMM_FORMAT, validateIsSpecialCharacter } from '@healthhub/react-ui';
import type { Dayjs } from 'dayjs';

import { Cluster } from '@shared/constants/constants';
import {
  APPOINTMENT_BOOK_NEXT_PATH,
  APPOINTMENT_MAKE_NEW_BOOK_POLYCLINIC_PATH,
  APPOINTMENT_RESCHEDULE_PATH,
} from '@shared/constants/paths';
import { QuestionType } from '@shared/services/appointment/form/types/appointmentForm.type';
import type {
  Appointment,
  AppointmentType,
  GetAppointmentLists,
  SupportingInformation,
} from '@shared/services/appointment/getLists/types/getAppointmentList.type';
import { SupportingInfoType } from '@shared/services/appointment/getLists/types/getAppointmentList.type';
import type { VcpItem } from '@shared/services/appointment/getLists/types/getAppointmentVCPDetails.type';
import type { TodayAppointment } from '@shared/services/appointment/getLists/types/getTodayAppointments.types';
import type {
  Doctor,
  DoctorGroup,
  DoctorPractitionerResult,
} from '@shared/services/appointment/makeNewNormal/types/getDoctors.type';
import type {
  GroupedServiceByName,
  Service,
} from '@shared/services/appointment/makeNewNormal/types/getServiceTypes.type';
import type { LinkedSlots, Slot } from '@shared/services/appointment/makeNewNormal/types/getSlotAppointment.type';
import type { TCUSlot } from '@shared/services/appointment/makeNewTCU/types/getTCUSlots.type';
import type { FormAnswerQuestion } from '@shared/services/appointment/questionnaire/types/getQuestionnaire.type';
import { GroupCode } from '@shared/utils/constants';

import { AppointmentGroupType, HashValue } from '@features/Appointment/types/appointment.types';

import { OTHERS_PATTERN, OTHERS_SORT_REPLACEMENT } from '../AppointmentMakeNew/constants/makeNew';
import type { RequestFormTypeType } from '../AppointmentMakeNew/types/makeNew';
import { RequestFormType } from '../AppointmentMakeNew/types/makeNew';
import {
  HTML_TAG_REGEX,
  MREG_AVAILABILIY,
  PRESUFFIX_TELECONSULT_CODES,
  PRESUFFIX_VIDEO_CONSULT_CODES,
  SOURCE_SYSTEM,
  TELECONSULT_CODES,
} from '../constants';

export const getAppointmentTab = (currentTab: string, AppointmentLists?: GetAppointmentLists) => {
  switch (currentTab) {
    case AppointmentGroupType.Miss:
      return AppointmentLists?.MissedAppointments || [];
    case AppointmentGroupType.Open:
      return AppointmentLists?.OpenAppointments || [];
    case AppointmentGroupType.Upcoming:
      return AppointmentLists?.UpcomingAppointments || [];
    default:
      return [];
  }
};

function getInstitutionNameOfPolyclinic(appointment: Appointment): string {
  if (appointment?.SourceSystem === SOURCE_SYSTEM.NGEMR) return appointment?.InstitutionName;
  return appointment?.Department ? appointment?.Department?.split('-')[0] : appointment?.Location;
}

export const getRestructureAppointmentsBySourceSystem = (appointments?: Appointment[]): Appointment[] | undefined => {
  if (!appointments) {
    return appointments;
  }
  return appointments.map((appointment) =>
    appointment.GroupCode === GroupCode.Polyclinic
      ? {
          ...appointment,
          ClinicInformation: {
            institutionName: getInstitutionNameOfPolyclinic(appointment),
            provider: appointment?.Provider,
            service: appointment?.ServiceType || appointment?.Specialty,
            time: appointment?.Start,
            apptDate: appointment?.ApptDate,
            apptTime: appointment?.ApptTime,
          },
        }
      : {
          ...appointment,
          ClinicInformation: {
            department: appointment.SourceSystem === SOURCE_SYSTEM.NGEMR ? appointment?.Department : '',
            institutionName: appointment?.InstitutionName,
            location:
              appointment.SourceSystem === SOURCE_SYSTEM.NGEMR
                ? appointment?.Location
                : appointment?.Department || appointment?.Location,
            provider: appointment?.Provider,
            service: appointment?.ServiceType || appointment?.Specialty,
            time: appointment?.Start,
            apptDate: appointment?.ApptDate,
            apptTime: appointment?.ApptTime,
          },
        }
  );
};

export function getLinkedAppointmentIds(appointments: Appointment[]) {
  return appointments?.map((appointment) => appointment?.AppointmentId);
}

export const getTelConsult = (appointmentType: AppointmentType) => {
  if (appointmentType?.Code?.length < 2) return '';

  const teleConsultPrefix = appointmentType?.Code?.slice(0, 2);
  const teleconsultSuffix = appointmentType?.Code?.slice(-2);

  if (
    PRESUFFIX_TELECONSULT_CODES.includes(teleConsultPrefix) ||
    PRESUFFIX_TELECONSULT_CODES.includes(teleconsultSuffix) ||
    PRESUFFIX_VIDEO_CONSULT_CODES.includes(teleConsultPrefix) ||
    PRESUFFIX_VIDEO_CONSULT_CODES.includes(teleconsultSuffix) ||
    TELECONSULT_CODES.includes(appointmentType?.Code)
  ) {
    return appointmentType?.Display;
  }
  return '';
};

export const isVideoConsult = (appointmentType?: AppointmentType) => {
  if (appointmentType && appointmentType?.Code?.length < 2) return false;

  const teleConsultPrefix = appointmentType?.Code?.slice(0, 2) ?? '';
  const teleConsultSuffix = appointmentType?.Code?.slice(-2) ?? '';

  return (
    PRESUFFIX_VIDEO_CONSULT_CODES.includes(teleConsultPrefix) ||
    PRESUFFIX_VIDEO_CONSULT_CODES.includes(teleConsultSuffix)
  );
};

export const isNuhsNhgVideoConsult = (appointment?: Appointment | null) => {
  if (!appointment) return false;

  // Check if it's NUHS or NHG cluster
  const isNuhsOrNhgCluster = appointment.ClusterCode === Cluster.NUHS || appointment.ClusterCode === Cluster.NHG;

  if (!isNuhsOrNhgCluster) return false;

  // Check IsVcSupported flag
  return appointment.IsVcSupported;
};

export function getShortMonthByNumber(monthNumber: number) {
  const month = dayjsWithTimezone().month(monthNumber);
  return month.format(MMM_FORMAT);
}

export function groupAppointmentsByLinkId(list: Appointment[]) {
  if (!list) return [];
  return list?.reduce<Appointment[]>((results: Appointment[], item) => {
    const existingAppointment: Appointment | undefined = results.find(
      (resultItem: Appointment) => resultItem.AppointmentLinkId === item.AppointmentLinkId
    );

    if (existingAppointment && item.AppointmentLinkId) {
      existingAppointment.AppointmentLinks.push({ ...item });
    } else {
      results.push({
        ...item,
        AppointmentLinks: [],
      });
    }
    return results;
  }, []);
}

export const getDentalApptDepositAppointmentGroup = (appointment?: Appointment) =>
  appointment
    ? [appointment, ...(appointment.AppointmentLinks ?? [])].map((groupedAppointment) => ({
        appointmentDate: groupedAppointment.Start,
        clusterCode: groupedAppointment.ClusterCode,
        serviceTypeId: groupedAppointment.VisitTypeId,
      }))
    : [];

export function groupSlotsByLinkId(list?: Slot[], enabledSortGroupedSlots = false, maxGroupedSlot = 25): LinkedSlots[] {
  if (!list) return [];

  const groupedSlotsMap: Record<string, Slot[]> = {};
  let groupedSlotCount = 1;
  let pageSet = 1; // will fix the grouping of slots evn though the slot link id is repeating
  let pendingNoPartner = true;

  list.forEach((slot) => {
    if (slot.IdentifierSlotLinkId) {
      if (!groupedSlotsMap[`${slot.IdentifierSlotLinkId}-${pageSet}`]) {
        groupedSlotsMap[`${slot.IdentifierSlotLinkId}-${pageSet}`] = [];
        groupedSlotsMap[`${slot.IdentifierSlotLinkId}-${pageSet}`].push(slot);
        pendingNoPartner = true;
        groupedSlotCount += 1;
      } else {
        groupedSlotsMap[`${slot.IdentifierSlotLinkId}-${pageSet}`].push(slot);
        pendingNoPartner = false;
      }

      if (groupedSlotCount === maxGroupedSlot && !pendingNoPartner) {
        groupedSlotCount = 1;
        pageSet += 1;
      }
    }
  });

  const linkedSlots = Object.keys(groupedSlotsMap).map((linkId) => ({
    IdentifierSlotLinkId: linkId,
    Slots: groupedSlotsMap[linkId],
  }));

  if (!enabledSortGroupedSlots) {
    return linkedSlots;
  }
  const sortedGroup = [...linkedSlots].sort((a, b) => {
    const startAFirstClinic = dayjsWithTimezone(a.Slots[0].Start);
    const startBFirstClinic = dayjsWithTimezone(b.Slots[0].Start);
    const startALinkedClinic = dayjsWithTimezone(a.Slots[1]?.Start);
    const startBLinkedClinic = dayjsWithTimezone(b.Slots[1]?.Start);

    if (startBFirstClinic.isBefore(startAFirstClinic)) return 1;
    if (startAFirstClinic.isBefore(startBFirstClinic)) return -1;
    if (startBLinkedClinic && startALinkedClinic) {
      if (startBLinkedClinic.isBefore(startALinkedClinic)) return 1;
      if (startALinkedClinic.isBefore(startBLinkedClinic)) return -1;
    }
    return 0;
  });
  return sortedGroup;
}

export const getAppointmentLinkSlotIdList = (appointmentLinks: Appointment[]) => {
  const appointmentLinkSlotIdList = [] as string[];

  appointmentLinks.forEach((appointment) => appointmentLinkSlotIdList.push(appointment.SlotId));

  return appointmentLinkSlotIdList;
};

export const getPathNameByHash = (hash: string) => {
  switch (hash) {
    case HashValue.bookNext:
      return APPOINTMENT_BOOK_NEXT_PATH;
    case HashValue.createNew:
      return APPOINTMENT_MAKE_NEW_BOOK_POLYCLINIC_PATH;
    default:
      return APPOINTMENT_RESCHEDULE_PATH;
  }
};

export const getNextPageRequestDataValue = (url?: string) => {
  if (!url) return '';
  return url.split('nextPageRequestData=')[1];
};

export const specialtySortKey = (name: string) =>
  OTHERS_PATTERN.test(name) ? name.replace(OTHERS_PATTERN, OTHERS_SORT_REPLACEMENT) : name;

export const groupSlotsByLinkedId = (TCUSlot: TCUSlot[]) =>
  TCUSlot?.reduce<TCUSlot[]>((results: TCUSlot[], item) => {
    const linkedSlot: TCUSlot | undefined = results.find(
      (slot) =>
        slot.IdentifierSlotLinkId &&
        item.IdentifierSlotLinkId &&
        slot.IdentifierSlotLinkId === item.IdentifierSlotLinkId
    );

    if (linkedSlot) {
      linkedSlot?.linkedSlot?.push({ ...item });
    } else {
      results.push({
        ...item,
        linkedSlot: [],
      });
    }
    return results;
  }, []);

export const addDoctorPrefix = (selectedDoctor: Doctor | null) => {
  if (!selectedDoctor) return '';
  const toTitleCase = (str: string) => str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  const trimmedPrefix = toTitleCase(selectedDoctor.Prefix.trim().replace(/\.$/, ''));
  const trimmedName = toTitleCase(selectedDoctor.Text.trim());
  return `${trimmedPrefix} ${trimmedName}`;
};

export const addFvSuffix = (selectedSpecialty?: string) => {
  if (!selectedSpecialty) return '';
  const suffix = '\u2013 First Visit';
  const trimmedName = selectedSpecialty?.trim();
  return `${trimmedName} ${suffix}`;
};

export const addPrivatePrefix = (value?: string) => {
  if (!value) return '';
  return `Private ${value.trim()}`;
};

export const addDoctorPrefixAndRole = (selectedDoctor: Doctor | null) => {
  if (!selectedDoctor) return '';
  const trimmedPrefix = selectedDoctor.Prefix.trim();
  const trimmedName = selectedDoctor.Text.trim();
  return `${trimmedPrefix} ${trimmedName} (${selectedDoctor.Role})`;
};

export const getDoctorsBySearchKey = ({
  doctorPractitionerResult,
  isFv = false,
  searchKey,
}: {
  doctorPractitionerResult?: DoctorPractitionerResult;
  isFv?: boolean;
  searchKey: string;
}): DoctorGroup => {
  const doctorGroups = doctorPractitionerResult?.Practitioners;
  const emptyDoctorGroup: DoctorGroup = {};
  const filteredDoctorGroups: DoctorGroup = {};
  const filterKey = Object.keys(doctorGroups || {});

  const isValidSearchKey = !validateIsSpecialCharacter(searchKey);
  if (!isValidSearchKey && searchKey.length) {
    if (!searchKey.includes('.')) {
      return emptyDoctorGroup;
    }
  }

  if (doctorGroups) {
    filterKey.forEach((category) => {
      const doctors = doctorGroups[category];
      doctors.forEach((doctor) => {
        const nameWithPrefix = isFv ? addDoctorPrefix(doctor) : addDoctorPrefixAndRole(doctor);
        if (nameWithPrefix.toLowerCase().includes(searchKey.toLowerCase())) {
          if (!filteredDoctorGroups[category]) {
            filteredDoctorGroups[category] = [];
          }
          filteredDoctorGroups[category].push(doctor);
        }
      });
    });
  }

  return filteredDoctorGroups || emptyDoctorGroup;
};

export const addPrefixOrganizationCode = (payload?: string) => {
  if (!payload) return '';
  return `Organization/${payload}`;
};

export const removePrefixOrganizationCode = (payload?: string) => {
  if (!payload) return '';
  return payload.split('Organization/')[1];
};

export const groupServiceByName = (services: Service[]): GroupedServiceByName[] => {
  const serviceTypeGroupedByNames = services.reduce<GroupedServiceByName[]>((serviceGrouped, curServiceType) => {
    const existingGroupedService = serviceGrouped.find((serviceType) => serviceType?.Name === curServiceType?.Name);
    if (existingGroupedService) {
      if (
        existingGroupedService?.ServiceTypeCodes !== null &&
        !existingGroupedService?.ServiceTypeCodes.includes(curServiceType.ServiceType)
      ) {
        existingGroupedService.ServiceTypeCodes.push(curServiceType.ServiceType);
      }
      existingGroupedService.ServicesByOrganization.push(curServiceType);
    } else {
      serviceGrouped.push({
        Name: curServiceType?.Name,
        ServicesByOrganization: [curServiceType],
        ServiceTypeCodes: [curServiceType?.ServiceType],
      });
    }
    return serviceGrouped;
  }, []);
  return serviceTypeGroupedByNames;
};

export function containsLiTag(str: string): boolean {
  const regex = /<li\b[^>]*>/i;
  return regex.test(str);
}

export const checkHtmlContainsString = (str: string): boolean => HTML_TAG_REGEX.test(str);

export const getOrganizationWithSupportingInfoType = (supportingInfo?: SupportingInformation[]): string | undefined => {
  if (!supportingInfo) return undefined;
  return supportingInfo?.find((info) => info.Type === SupportingInfoType.Organization)?.Reference;
};

export function getContentInHTMLTag(content: string) {
  const pattern = /<b>(.*?)<\/b>/;
  const match = pattern.exec(content);
  if (match) {
    return match[1];
  }
  return content;
}

function encodeStringToBytes(input: string): string {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(input);
  return Array.from(bytes)
    .map((byte) => byte.toString())
    .join(' ');
}

export const convertStringToByteCode = (text: string) =>
  checkHtmlContainsString(text) ? encodeStringToBytes(text) : text;

export function convertValuesToDayjs(values: FormAnswerQuestion, typeQuestion: string): FormAnswerQuestion {
  if (typeQuestion !== QuestionType.Date) return values;
  const convertedValues: FormAnswerQuestion = {};
  Object.entries(values).forEach(([key, value]) => {
    if (typeQuestion === QuestionType.Date) {
      convertedValues[key] = dayjsWithTimezone(value as Dayjs);
    }
  });
  return convertedValues;
}

export const removeHtmlInString = (text: string) => {
  if (checkHtmlContainsString(text)) {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.body.textContent ?? '';
  }
  return text;
};

export const getAppointmentType = (value: string): RequestFormTypeType => {
  switch (value) {
    case 'Book a new Appointment':
      return RequestFormType.Make;
    case 'Cancel an Existing Appointment':
      return RequestFormType.Cancel;
    case 'Change an Existing Appointment':
      return RequestFormType.Change;
    default:
      return '' as RequestFormTypeType;
  }
};

const resolveServicesString = (
  servicesByInstitution: Record<string, string> | string | undefined,
  referredBy: string
): string => {
  if (!servicesByInstitution) return '';
  if (typeof servicesByInstitution === 'string') return servicesByInstitution;
  return servicesByInstitution[referredBy.toUpperCase()] ?? servicesByInstitution?.OTHERS ?? '';
};

export const getServicesFromServiceSystem = ({
  organizationCode,
  referredBy,
  serviceSystem,
}: {
  organizationCode: string;
  referredBy: string;
  serviceSystem: string;
}): string[] => {
  try {
    if (!serviceSystem || !organizationCode) return [];
    const parsedServiceSystem = JSON.parse(serviceSystem);
    const servicesByInstitution = parsedServiceSystem[organizationCode] ?? parsedServiceSystem.Others;
    const services = resolveServicesString(servicesByInstitution, referredBy);
    return services ? services.split('|') : [];
  } catch (error) {
    console.warn('Error parsing getServicesFromServiceSystem:', error);
    return [];
  }
};

export const getServicesFromHBServiceSystem = ({
  organizationCode,
  referredBy,
  serviceSystem,
}: {
  organizationCode: string;
  referredBy: string;
  serviceSystem: string;
}): string[] => {
  try {
    if (!serviceSystem || !organizationCode) return [];
    const parsedServiceSystem = JSON.parse(serviceSystem);
    const servicesByInstitution = parsedServiceSystem[organizationCode];
    const services = resolveServicesString(servicesByInstitution, referredBy);
    return services ? services.split('|') : [];
  } catch (error) {
    console.warn('Error parsing getServicesFromHBServiceSystem:', error);
    return [];
  }
};

export const getCountriesFromHBServiceSystem = ({ serviceSystem }: { serviceSystem: string }): string[] => {
  try {
    if (!serviceSystem) return [];
    return JSON.parse(serviceSystem);
  } catch (error) {
    console.warn('Error parsing getCountriesFromHBServiceSystem:', error);
    return [];
  }
};

export const getInstitutionsFromServiceSystem = ({
  serviceSystem,
}: {
  serviceSystem: string;
}): Record<'EndOffset' | 'InstitutionCode' | 'InstitutionName' | 'StartOffset', string>[] => {
  try {
    if (!serviceSystem) return [];
    return JSON.parse(serviceSystem);
  } catch (error) {
    console.warn('Error parsing getInstitutionsFromServiceSystem:', error);
    return [];
  }
};

export const convertTodayAppointmentToAppointment = ({
  todayAppointment,
}: {
  todayAppointment: TodayAppointment;
}): Appointment =>
  ({
    AppointmentId: todayAppointment.AppointmentId,
    AppointmentLinkId: '',
    AppointmentLinks: [],
    AppointmentStatus: todayAppointment.Status,
    AppointmentType: {
      Code: '',
      Display: '',
      Extension: [],
      Text: '',
    },
    ApptDate: todayAppointment.ApptDate,
    ApptTime: todayAppointment.ApptTime,
    CallSlotSearchDateRangeSeparately: false,
    CanAddVisitor: todayAppointment?.CanAddVisitor,
    CanCancel: false,
    CanMake: false,
    CanRegister: false,
    CanReschedule: false,
    ClinicCode: '',
    ClusterCode: todayAppointment.ClusterCode,
    Comment: null,
    Department: todayAppointment.Department,
    Description: '',
    End: todayAppointment.AppointmentDateTime,
    InstitutionName: todayAppointment.InstitutionName,
    IsBundledAppointment: todayAppointment.IsBundledAppointment,
    IsHsg: todayAppointment.IsHsg,
    IsVcSupported: todayAppointment.IsVcSupported,
    Location: todayAppointment.Location,
    Logo: todayAppointment.Logo,
    ParticipantLocation: '',
    PatientInstruction: null,
    Provider: todayAppointment.Provider,
    QuestionnaireStatus: [
      {
        QuestionnaireId: '',
        QuestionnaireStatus: '',
      },
    ],
    RescheduleMessage: null,
    ServiceType: '',
    SlotHelpInfo: '',
    SlotId: '',
    SlotSearchDateRangeFrom: null,
    SlotSearchDateRangeTo: null,
    SourceSystem: '',
    Specialty: '',
    Start: todayAppointment.AppointmentDateTime,
    SupportingInfo: [],
    VisitTypeId: '',
  }) as Appointment;

export const formatLinkedSlotId = (linkedSlots: TCUSlot): string => {
  let LinkedSlotIds: null | string[] = null;

  if (linkedSlots.linkedSlot) {
    LinkedSlotIds = linkedSlots.linkedSlot.map((slot) => slot.SlotId);
  }
  return LinkedSlotIds ? [linkedSlots.SlotId, ...LinkedSlotIds].join(',') : linkedSlots.SlotId;
};

export const displayLocation = (clusterCode: string, location?: string, institutionName?: string): string => {
  if (clusterCode === Cluster.NHG || clusterCode === Cluster.NUHS) {
    return institutionName || '';
  }
  return location || institutionName || '';
};

export const decodeToHtml = (encodedString: string): string => {
  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(encodedString);

  // skip if already decoded
  if (hasHtmlTags) {
    return encodedString;
  }

  const parser = new DOMParser();
  const decodedString = parser.parseFromString(encodedString, 'text/html').documentElement.textContent;
  return decodedString || '';
};

export const getBase64FromFile = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export const getSortedAppointments = (appointments: (Appointment | null | undefined)[]): Appointment[] =>
  appointments
    .filter((appt): appt is Appointment => appt !== null && appt !== undefined)
    .sort((a, b) => {
      const timeA = dayjsWithTimezone(a.Start).isValid() ? dayjsWithTimezone(a.Start).valueOf() : 0;
      const timeB = dayjsWithTimezone(b.Start).isValid() ? dayjsWithTimezone(b.Start).valueOf() : 0;
      return timeA - timeB;
    });

export const formatIntervalTimer = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Checks if the registration availability status is pending
 */
const isMregStatusPending = (status?: string): boolean =>
  status !== undefined && status?.trim() !== '' && status?.toLowerCase() === MREG_AVAILABILIY.pending;

export const isVcAppointmentRegistered = (params: {
  appointment: Appointment;
  isManualRegInstitution?: boolean;
  isUpcomingTab?: boolean;
  todayAppointment?: TodayAppointment;
  vcpItem?: VcpItem;
}): boolean => {
  const { appointment, todayAppointment, isUpcomingTab = true, vcpItem, isManualRegInstitution = false } = params;

  if (!isUpcomingTab) return false;

  const isNgemr = appointment?.SourceSystem === SOURCE_SYSTEM.NGEMR;
  const isNgemrPolyclinics = isNgemr && appointment?.GroupCode === GroupCode.Polyclinic;
  const isEnableMreg = true;
  const queueNumber = appointment?.SupportingInfo?.find((info) => info.Type === SupportingInfoType.Encounter)?.Id;

  return (
    (isNgemrPolyclinics &&
      isEnableMreg &&
      !isMregStatusPending(appointment?.RegistrationAvailability) &&
      !!queueNumber &&
      !!appointment?.EncounterId) ||
    (appointment?.IsCeqmsPoly &&
      isEnableMreg &&
      !!todayAppointment?.EncounterId &&
      !isMregStatusPending(todayAppointment.RegistrationAvailability)) ||
    (!appointment?.IsCeqmsPoly &&
      !!todayAppointment?.EncounterId &&
      !isMregStatusPending(todayAppointment.RegistrationAvailability)) ||
    (isManualRegInstitution && !!vcpItem?.IsRegistered)
  );
};

export const isNhgVcAppointment = (appointment?: Appointment | null): boolean =>
  appointment?.ClusterCode === Cluster.NHG && appointment?.IsVcSupported;

export const parseManualRegistrationConfig = (config?: string): Record<string, number> => {
  if (!config) return {};
  return config.split(',').reduce<Record<string, number>>((acc, entry) => {
    const [code, minutes] = entry.trim().split(':');
    if (code?.trim()) {
      acc[code.trim()] = parseInt(minutes ?? '0', 10);
    }
    return acc;
  }, {});
};

const resolveManualRegWindow = (appointment: Appointment | null | undefined, configMap: Record<string, number>) => {
  const institutionCode =
    appointment?.SupportingInfo?.find((info) => info.Type === SupportingInfoType.Organization)?.Id ??
    appointment?.OrganizationUnit;
  if (!institutionCode || !(institutionCode in configMap) || !appointment?.Start) return null;
  const bufferMins = configMap[institutionCode];
  const now = dayjsWithTimezone();
  const apptStart = dayjsWithTimezone(appointment.Start);
  return {
    now,
    windowStart: apptStart.subtract(bufferMins, 'minutes'),
    windowEnd: apptStart.add(bufferMins, 'minutes'),
  };
};

export const isVcWithinManualRegistrationWindow = (
  appointment: Appointment | null | undefined,
  configMap: Record<string, number>
): boolean => {
  const bounds = resolveManualRegWindow(appointment, configMap);
  return !!bounds && bounds.now.isBetween(bounds.windowStart, bounds.windowEnd);
};

export const isVcPastManualRegistrationWindow = (
  appointment: Appointment | null | undefined,
  configMap: Record<string, number>
): boolean => {
  const bounds = resolveManualRegWindow(appointment, configMap);
  return !!bounds && bounds.now.isAfter(bounds.windowEnd);
};

export const shouldShowRegisterForGeneral = (params: {
  appointment?: Appointment | null;
  todayAppointment?: null | TodayAppointment;
}): boolean => {
  const { appointment, todayAppointment } = params;
  const isSoc = appointment?.GroupCode !== GroupCode.Polyclinic;
  return isSoc && isMregStatusPending(todayAppointment?.RegistrationAvailability) && !appointment?.IsMregDisabled;
};

export const shouldShowRegisterForPolyclinics = (params: {
  appointment?: Appointment | null;
  todayAppointment?: null | TodayAppointment;
}): boolean => {
  const { appointment, todayAppointment } = params;
  const isPolyclinics = appointment?.GroupCode === GroupCode.Polyclinic;

  if (!isPolyclinics || appointment?.IsMregDisabled) return false;

  const queueNumber = appointment?.SupportingInfo?.find((info) => info.Type === SupportingInfoType.Encounter)?.Id;

  return (
    (appointment.IsCeqmsPoly && isMregStatusPending(todayAppointment?.RegistrationAvailability)) ||
    appointment?.CanRegister ||
    (!!queueNumber && !!appointment?.EncounterId && isMregStatusPending(appointment?.RegistrationAvailability))
  );
};
